import { useParams, Link } from "@tanstack/react-router"
import { useCertificate } from "@/lib/queries"
import { ApiError } from "@/lib/api"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Loader2, Download, Award } from "lucide-react"

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  })
}

export function CertificatePage() {
  const { attemptId } = useParams({ strict: false })
  const attemptIdNum = attemptId ? Number(attemptId) : undefined

  const { data: cert, isLoading, isError, error } = useCertificate(attemptIdNum)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="mr-2 h-5 w-5 animate-spin text-muted-foreground" />
        <span className="text-muted-foreground">Loading certificate…</span>
      </div>
    )
  }

  if (isError || !cert || attemptIdNum === undefined) {
    const message =
      error instanceof ApiError && (error.status === 422 || error.status === 404)
        ? "A certificate is only available for passed assessments."
        : "This certificate could not be loaded."
    return (
      <div className="flex justify-center p-8">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Certificate Unavailable</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">{message}</p>
            <Button asChild variant="outline">
              <Link to="/training">Back to My Training</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center p-8 print:p-0">
      {/* Download button — hidden when printing */}
      <div className="mb-6 flex w-full max-w-2xl items-center justify-between print:hidden">
        <Button asChild variant="outline" size="sm">
          <Link to="/training">Back to My Training</Link>
        </Button>
        <Button asChild size="sm" variant="outline">
          <a
            href={`/api/v1/customer/quiz_attempts/${attemptIdNum}/certificate_pdf`}
            target="_blank"
            rel="noopener"
          >
            <Download className="mr-2 h-4 w-4" />
            Download PDF
          </a>
        </Button>
      </div>

      {/* Certificate card */}
      <div className="w-full max-w-2xl rounded-lg border-2 border-border bg-card p-10 text-center shadow-sm print:border print:shadow-none">
        <div className="mb-6 flex justify-center">
          <Award className="h-14 w-14 text-emerald-600" />
        </div>

        <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          Certificate of Completion
        </p>

        <h1 className="mt-4 text-3xl font-bold tracking-tight">
          {cert.staff_name}
        </h1>

        <p className="mt-3 text-base text-muted-foreground">
          has successfully completed
        </p>

        <h2 className="mt-3 text-xl font-semibold">{cert.quiz_title}</h2>

        {/* The mark itself is deliberately absent: a certificate records
            that competency was demonstrated, not the margin by which. */}
        <div className="mx-auto mt-6 flex max-w-sm flex-col items-center gap-1">
          <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-sm font-semibold text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
            Passed
          </span>
        </div>

        <div className="mt-8 flex flex-col items-center gap-1 text-sm text-muted-foreground">
          <p>
            Completed on{" "}
            <span className="font-medium text-foreground">
              {formatDate(cert.completed_on)}
            </span>
          </p>
          <p>Version: {cert.version_label}</p>
        </div>
      </div>
    </div>
  )
}
